/**
 * WebSocket-based Event-Driven Checkout System
 * Scalable solution for tracking hundreds of document checkout states
 * Uses existing WebSocket infrastructure for real-time updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import io, { Socket } from 'socket.io-client';

export interface CheckoutState {
  isCheckedOut: boolean;
  checkedOutBy: {
    user_id: string;
    keycloak_user_sub: string;
    role: string;
    name?: string;
  } | null;
  checkedOutAt: string | null;
  checkedOutExpiresAt: string | null;
  isLocked: boolean; // Simple lock state - true means document is locked
}

export interface CheckoutEvent {
  type: 'checkout' | 'checkin' | 'force_checkin' | 'expire' | 'auto_checkin' | 'timer_update';
  documentId: string;
  versionId?: string;
  userId?: string;
  timestamp: string;
  isCheckedOut?: boolean;
  checkedOutAt?: string | null;
  expiresAt?: string | null;
  checkedOutBy?: {
    user_id: string;
    keycloak_user_sub?: string;
    name: string;
    role?: string;
  } | null;
  remainingTime?: {
    hours: number;
    minutes: number;
    seconds: number;
    totalSeconds: number;
  };
  data?: any;
}

interface UseWebSocketCheckoutOptions {
  documentId: string;
  versionId?: string; // Optional for backward compatibility
  onCheckoutChange?: (state: CheckoutState) => void;
  onError?: (error: string) => void;
}

interface UseWebSocketCheckoutReturn {
  checkoutState: CheckoutState;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  checkout: (durationHours?: number) => Promise<void>;
  checkin: (options?: { createNewVersion?: boolean; versionComment?: string }) => Promise<void>;
  forceCheckin: (reason?: string) => Promise<void>;
  
  // Computed state
  isCheckedOutByMe: boolean;
  isCheckedOutByOther: boolean;
  canCheckout: boolean;
  canCheckin: boolean;
  canForceCheckin: boolean;
}

// Global WebSocket connection manager
class CheckoutWebSocketManager {
  private socket: Socket | null = null;
  private subscribers = new Map<string, Set<(state: CheckoutState) => void>>();
  private documentStates = new Map<string, CheckoutState>();
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private isAuthenticated = false;
  private authToken: string | null = null;

  constructor() {
    // Don't auto-connect - wait for authentication
    this.checkAuthenticationStatus();
  }

  /**
   * Check if user is authenticated before connecting
   */
  private checkAuthenticationStatus(): void {
    // Check for Keycloak token in localStorage
    const keycloakToken = localStorage.getItem('kc-token');
    const keycloakRefreshToken = localStorage.getItem('kc-refresh-token');
    
    if (keycloakToken && keycloakRefreshToken) {
      try {
        // Basic token validation - check if not expired
        const tokenPayload = JSON.parse(atob(keycloakToken.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        
        if (tokenPayload.exp > currentTime) {
          this.isAuthenticated = true;
          this.authToken = keycloakToken;
          console.log('[CheckoutWebSocket] Authentication detected, connecting...');
          this.connect();
        } else {
          console.log('[CheckoutWebSocket] Token expired, waiting for refresh...');
          this.waitForAuthentication();
        }
      } catch (error) {
        console.log('[CheckoutWebSocket] Invalid token format, waiting for authentication...');
        this.waitForAuthentication();
      }
    } else {
      console.log('[CheckoutWebSocket] No authentication found, waiting for login...');
      this.waitForAuthentication();
    }
  }

  /**
   * Wait for authentication by polling localStorage
   */
  private waitForAuthentication(): void {
    const checkAuth = () => {
      const keycloakToken = localStorage.getItem('kc-token');
      if (keycloakToken && !this.isAuthenticated) {
        console.log('[CheckoutWebSocket] Authentication detected, initializing connection...');
        this.isAuthenticated = true;
        this.authToken = keycloakToken;
        this.connect();
      } else if (!keycloakToken && this.isAuthenticated) {
        // User logged out
        console.log('[CheckoutWebSocket] User logged out, disconnecting...');
        this.handleLogout();
      }
    };

    // Check every 2 seconds for authentication
    setInterval(checkAuth, 2000);
  }

  /**
   * Handle user logout
   */
  private handleLogout(): void {
    this.isAuthenticated = false;
    this.authToken = null;
    this.disconnect();
    this.subscribers.clear();
    this.documentStates.clear();
  }

  private connect() {
    if (this.socket?.connected || !this.isAuthenticated || !this.authToken) {
      console.log('[CheckoutWebSocket] Skipping connection - not authenticated or already connected');
      return;
    }

    console.log('[CheckoutWebSocket] Connecting to checkout events with authentication...');
    
    this.socket = io('/checkout', {
      transports: ['websocket'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      auth: {
        token: this.authToken
      },
      extraHeaders: {
        'Authorization': `Bearer ${this.authToken}`
      }
    });

    this.socket.on('connect', () => {
      console.log('✅ [CheckoutWebSocket] Connected to checkout events successfully');
      console.log(`🔗 [CheckoutWebSocket] Socket ID: ${this.socket?.id}`);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // Re-subscribe to all documents
      const documentIds = Array.from(this.subscribers.keys());
      if (documentIds.length > 0) {
        console.log(`📋 [CheckoutWebSocket] Re-subscribing to ${documentIds.length} documents:`, documentIds);
        this.socket?.emit('subscribe_documents', documentIds);
      }
    });

    this.socket.on('disconnect', () => {
      console.log('[CheckoutWebSocket] Disconnected from checkout events');
      this.isConnected = false;
    });

    this.socket.on('checkout_event', (event: CheckoutEvent) => {
      console.log('[CheckoutWebSocket] Received checkout event:', event);
      this.handleCheckoutEvent(event);
    });

    this.socket.on('document_state', (data: { documentId: string; state: CheckoutState }) => {
      console.log('[CheckoutWebSocket] Received document state:', data);
      this.updateDocumentState(data.documentId, data.state);
    });

    this.socket.on('connect_error', (error) => {
      console.error('[CheckoutWebSocket] Connection error:', error);
      this.reconnectAttempts++;
      
      // If authentication fails, don't keep retrying
      if (error.message?.includes('authentication') || error.message?.includes('unauthorized')) {
        console.log('[CheckoutWebSocket] Authentication error, stopping reconnection attempts');
        this.disconnect();
        this.isAuthenticated = false;
        this.authToken = null;
      }
    });
  }

  private handleCheckoutEvent(event: CheckoutEvent) {
    const { type, userId, timestamp } = event;
    const stateKey = event.versionId ? `${event.documentId}:version:${event.versionId}` : event.documentId;
    
    let newState: CheckoutState;
    
    switch (type) {
      case 'checkout':
        newState = {
          isCheckedOut: event.isCheckedOut ?? true,
          checkedOutBy: event.checkedOutBy ? {
            user_id: event.checkedOutBy.user_id,
            keycloak_user_sub: event.checkedOutBy.keycloak_user_sub || event.checkedOutBy.user_id,
            role: event.checkedOutBy.role || 'user',
            name: event.checkedOutBy.name,
          } : {
            user_id: userId || '',
            keycloak_user_sub: userId || '',
            role: event.data?.role || 'user',
            name: event.data?.name,
          },
          checkedOutAt: event.checkedOutAt || timestamp,
          checkedOutExpiresAt: event.expiresAt || event.data?.expiresAt || null,
          isLocked: true
        };
        break;
        
      case 'checkin':
      case 'force_checkin':
      case 'expire':
      case 'auto_checkin':
        newState = {
          isCheckedOut: false,
          checkedOutBy: null,
          checkedOutAt: null,
          checkedOutExpiresAt: null,
          isLocked: false
        };
        break;

      case 'timer_update':
        return;
        
      default:
        return;
    }
    
    this.updateDocumentStateInternal(stateKey, newState);
  }

  private updateDocumentStateInternal(documentId: string, state: CheckoutState) {
    this.documentStates.set(documentId, state);
    
    // Notify all subscribers for this document
    const subscribers = this.subscribers.get(documentId);
    if (subscribers) {
      subscribers.forEach(callback => callback(state));
    }
  }

  /**
   * Public method to manually update document state (fallback for WebSocket failures)
   */
  public updateDocumentState(documentId: string, state: CheckoutState) {
    console.log(`🔄 [CheckoutWebSocketManager] Manually updating state for document ${documentId}`);
    this.updateDocumentStateInternal(documentId, state);
  }

  subscribe(documentId: string, callback: (state: CheckoutState) => void) {
    if (!this.subscribers.has(documentId)) {
      this.subscribers.set(documentId, new Set());
      
      // Subscribe to this document on the server
      if (this.isConnected) {
        this.socket?.emit('subscribe_document', documentId);
      }
    }
    
    this.subscribers.get(documentId)?.add(callback);
    
    // Return current state if available
    const currentState = this.documentStates.get(documentId);
    if (currentState) {
      callback(currentState);
    } else {
      // Request current state from server
      this.socket?.emit('get_document_state', documentId);
    }
  }

  unsubscribe(documentId: string, callback: (state: CheckoutState) => void) {
    const subscribers = this.subscribers.get(documentId);
    if (subscribers) {
      subscribers.delete(callback);
      
      // If no more subscribers, unsubscribe from server
      if (subscribers.size === 0) {
        this.subscribers.delete(documentId);
        if (this.isConnected) {
          this.socket?.emit('unsubscribe_document', documentId);
        }
        this.documentStates.delete(documentId);
      }
    }
  }

  getDocumentState(documentId: string): CheckoutState | null {
    return this.documentStates.get(documentId) || null;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.subscribers.clear();
    this.documentStates.clear();
    this.isConnected = false;
  }

  /**
   * Manually trigger authentication check (useful for token refresh)
   */
  public refreshAuthentication(): void {
    console.log('[CheckoutWebSocket] Refreshing authentication...');
    this.checkAuthenticationStatus();
  }

  /**
   * Get connection status
   */
  public getConnectionStatus(): { isConnected: boolean; isAuthenticated: boolean } {
    return {
      isConnected: this.isConnected,
      isAuthenticated: this.isAuthenticated
    };
  }
}

// Global instance
const checkoutManager = new CheckoutWebSocketManager();

// Export manager for external access
export { checkoutManager };

export function useWebSocketCheckout({
  documentId,
  versionId,
  onCheckoutChange,
  onError
}: UseWebSocketCheckoutOptions): UseWebSocketCheckoutReturn {
  const { keycloak } = useKeycloak();
  
  const [checkoutState, setCheckoutState] = useState<CheckoutState>({
    isCheckedOut: false,
    checkedOutBy: null,
    checkedOutAt: null,
    checkedOutExpiresAt: null,
    isLocked: false
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const callbackRef = useRef<(state: CheckoutState) => void>(() => {});

  // Create stable callback reference
  useEffect(() => {
    callbackRef.current = (state: CheckoutState) => {
      setCheckoutState(state);
      onCheckoutChange?.(state);
    };
  }, [onCheckoutChange]);

  // Subscribe to document checkout events AND fetch initial state
  useEffect(() => {
    if (!documentId || !callbackRef.current || !keycloak?.token) return;

    // Create a unique key for version-specific or document-level checkout
    const stateKey = versionId ? `${documentId}:version:${versionId}` : documentId;

    const callback = callbackRef.current;
    checkoutManager.subscribe(stateKey, callback);

    // CRITICAL FIX: Fetch initial checkout state from database
    const fetchInitialState = async () => {
      try {
        console.log(`🔍 [WebSocketCheckout] Fetching initial checkout state for document ${documentId}`);
        
        // Use version-specific endpoint if versionId is provided, otherwise use document-level
        const statusUrl = versionId 
          ? `/api/v1/dms/documents/versions/${versionId}/checkout-status`
          : `/api/v1/dms/documents/${documentId}/checkout-status`;
        
        console.log(`🔍 [WebSocketCheckout] Fetching initial checkout state from: ${statusUrl}`);
        
        const response = await fetch(statusUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${keycloak.token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`📊 [WebSocketCheckout] Initial state fetched:`, data);
          
          if (data.checkoutStatus && data.checkoutStatus.isCheckedOut) {
            // Document is already checked out - update state immediately
            const initialState: CheckoutState = {
              isCheckedOut: data.checkoutStatus.isCheckedOut,
              checkedOutBy: data.checkoutStatus.checkedOutBy,
              checkedOutAt: data.checkoutStatus.checkedOutAt,
              checkedOutExpiresAt: data.checkoutStatus.checkedOutExpiresAt,
              isLocked: data.checkoutStatus.isCheckedOut
            };
            
            console.log(`✅ [WebSocketCheckout] Setting initial checkout state:`, initialState);
            checkoutManager.updateDocumentState(stateKey, initialState);
          } else {
            // Document is not checked out
            const initialState: CheckoutState = {
              isCheckedOut: false,
              checkedOutBy: null,
              checkedOutAt: null,
              checkedOutExpiresAt: null,
              isLocked: false
            };
            
            console.log(`✅ [WebSocketCheckout] Setting initial available state:`, initialState);
            checkoutManager.updateDocumentState(stateKey, initialState);
          }
        } else if (response.status === 404) {
          // Document not found or not checked out - set as available
          const initialState: CheckoutState = {
            isCheckedOut: false,
            checkedOutBy: null,
            checkedOutAt: null,
            checkedOutExpiresAt: null,
            isLocked: false
          };
          
          console.log(`✅ [WebSocketCheckout] Document not checked out, setting available state`);
          checkoutManager.updateDocumentState(stateKey, initialState);
        } else {
          console.warn(`⚠️ [WebSocketCheckout] Failed to fetch initial state: ${response.status}`);
        }
      } catch (error) {
        console.error(`❌ [WebSocketCheckout] Error fetching initial state:`, error);
      }
    };

    // Fetch initial state immediately
    fetchInitialState();

    return () => {
      checkoutManager.unsubscribe(stateKey, callback);
    };
  }, [documentId, versionId, keycloak?.token]);

  // Computed state
  const isCheckedOutByMe = checkoutState.isCheckedOut && 
    (checkoutState.checkedOutBy?.user_id === keycloak?.subject || 
     checkoutState.checkedOutBy?.keycloak_user_sub === keycloak?.subject);
  const isCheckedOutByOther = checkoutState.isCheckedOut && !isCheckedOutByMe;
  
  // DEBUG: Log user comparison for troubleshooting
  if (checkoutState.isCheckedOut) {
    console.log('🔍 [WebSocketCheckout] User comparison debug:', {
      checkedOutByUserId: checkoutState.checkedOutBy?.user_id,
      checkedOutByKeycloakSub: checkoutState.checkedOutBy?.keycloak_user_sub,
      keycloakSubject: keycloak?.subject,
      isCheckedOutByMe,
      isCheckedOutByOther
    });
  }
  const canCheckout = !checkoutState.isLocked && !isLoading;
  const canCheckin = isCheckedOutByMe && !isLoading;
  const canForceCheckin = keycloak?.hasRealmRole('Administrator') && isCheckedOutByOther;

  // Actions
  const checkout = useCallback(async (durationHours: number = 24) => {
    console.log(`🚀 [WebSocketCheckout] Starting checkout for document ${documentId}, duration: ${durationHours}h`);
    console.log(`🔐 [WebSocketCheckout] Auth status - canCheckout: ${canCheckout}, token exists: ${!!keycloak.token}`);
    
    if (!canCheckout) {
      console.warn('⚠️ [WebSocketCheckout] Cannot checkout - canCheckout is false');
      return;
    }
    
    if (!keycloak.token) {
      console.error('❌ [WebSocketCheckout] No authentication token available');
      setError('Authentication required');
      onError?.('Authentication required');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Use version-specific endpoint if versionId is provided, otherwise use document-level
      const requestUrl = versionId 
        ? `/api/v1/dms/documents/versions/${versionId}/checkout`
        : `/api/v1/dms/documents/${documentId}/checkout`;
      const requestBody = {
        durationHours,
        sessionId: `session_${Date.now()}`,
        deviceInfo: navigator.userAgent
      };
      
      console.log(`📡 [WebSocketCheckout] Making ${versionId ? 'version-specific' : 'document-level'} request to: ${requestUrl}`);
      console.log(`📦 [WebSocketCheckout] Request body:`, requestBody);
      
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log(`📨 [WebSocketCheckout] Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ [WebSocketCheckout] Server error response:', errorData);
        throw new Error(errorData.message || `Failed to checkout: ${response.statusText}`);
      }

      const responseData = await response.json().catch(() => ({}));
      console.log('✅ [WebSocketCheckout] Checkout successful, response:', responseData);
      
      const { isConnected } = checkoutManager.getConnectionStatus();
      if (!isConnected) {
        console.log('🔄 [WebSocketCheckout] WebSocket offline; applying checkout fallback state');
        const newState: CheckoutState = {
          isCheckedOut: true,
          checkedOutBy: {
            user_id: keycloak.subject || '',
            keycloak_user_sub: keycloak.subject || '',
            role: 'user',
            name: keycloak.tokenParsed?.name || 'Current User'
          },
          checkedOutAt: new Date().toISOString(),
          checkedOutExpiresAt: responseData.expiresAt || new Date(Date.now() + durationHours * 60 * 60 * 1000).toISOString(),
          isLocked: true
        };
        const stateKey = versionId ? `${documentId}:version:${versionId}` : documentId;
        checkoutManager.updateDocumentState(stateKey, newState);
      }
      
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Checkout failed';
      console.error('❌ [WebSocketCheckout] Checkout failed with error:', err);
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [documentId, versionId, keycloak.token, keycloak.subject, keycloak.tokenParsed?.name, canCheckout, onError]);

  const checkin = useCallback(async (options = {}) => {
    if (!canCheckin) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Use version-specific endpoint if versionId is provided, otherwise use document-level
      const requestUrl = versionId 
        ? `/api/v1/dms/documents/versions/${versionId}/checkin`
        : `/api/v1/dms/documents/${documentId}/checkin`;
      
      console.log(`📡 [WebSocketCheckout] Making ${versionId ? 'version-specific' : 'document-level'} checkin request to: ${requestUrl}`);
      
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          createNewVersion: false,
          versionComment: 'Document check-in',
          ...options
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to checkin: ${response.statusText}`);
      }

      // WebSocket will handle state update via event
      console.log('✅ [WebSocketCheckout] Document checkin initiated');
      
      const { isConnected } = checkoutManager.getConnectionStatus();
      if (!isConnected) {
        console.log('🔄 [WebSocketCheckout] WebSocket offline; applying checkin fallback state');
        const newState: CheckoutState = {
          isCheckedOut: false,
          checkedOutBy: null,
          checkedOutAt: null,
          checkedOutExpiresAt: null,
          isLocked: false
        };
        const stateKey = versionId ? `${documentId}:version:${versionId}` : documentId;
        checkoutManager.updateDocumentState(stateKey, newState);
      }
      
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Checkin failed';
      setError(errorMsg);
      onError?.(errorMsg);
      console.error('❌ [WebSocketCheckout] Checkin failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [documentId, versionId, keycloak.token, canCheckin, onError]);

  const forceCheckin = useCallback(async (reason = 'Administrator override') => {
    if (!canForceCheckin) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/v1/dms/documents/${documentId}/force-checkin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to force checkin: ${response.statusText}`);
      }

      // WebSocket will handle state update via event
      console.log('✅ [WebSocketCheckout] Force checkin initiated');
      
      const { isConnected } = checkoutManager.getConnectionStatus();
      if (!isConnected) {
        console.log('🔄 [WebSocketCheckout] WebSocket offline; applying force-checkin fallback state');
        const newState: CheckoutState = {
          isCheckedOut: false,
          checkedOutBy: null,
          checkedOutAt: null,
          checkedOutExpiresAt: null,
          isLocked: false
        };
        const stateKey = versionId ? `${documentId}:version:${versionId}` : documentId;
        checkoutManager.updateDocumentState(stateKey, newState);
      }
      
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Force checkin failed';
      setError(errorMsg);
      onError?.(errorMsg);
      console.error('❌ [WebSocketCheckout] Force checkin failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [documentId, versionId, keycloak.token, canForceCheckin, onError]);

  return {
    checkoutState,
    isLoading,
    error,
    checkout,
    checkin,
    forceCheckin,
    isCheckedOutByMe,
    isCheckedOutByOther,
    canCheckout,
    canCheckin,
    canForceCheckin
  };
}

// Cleanup on app unmount
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    checkoutManager.disconnect();
  });
}

export default useWebSocketCheckout;
